import type { SATUserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import {
  MINIMUM_LEARNER_CROSSWALK_ID,
  MINIMUM_TUTOR_CROSSWALK_ID,
} from '@/client/services/satBootcamp/constants'

import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  queryLimitToSQL,
} from './common'
import type { SATUserType } from './constants'
import UserQ from './UserQ'

const SATUserQ = {
  ...makeQueryModelV2<SATUserAttributes>('sat_user')([
    'id',
    'user_id',
    'type',
    'crosswalk_id',
    'first_name',
    'last_name',
    'email',
    'parent_first_name',
    'parent_last_name',
    'parent_email',
    'eligible_math',
    'eligible_reading',
    'invite_code',
    'parent_consent_requested_at',
    'parent_consent_completed_at',
    'parent_consent_data',
    'assent_completed_at',
    'assent_data',
    'last_viewed_at',
    'phone_number',
    'parent_last_viewed_at',
    'phone_number_waived_at',
    'internal_state',
    'created_at',
    'deleted_at',
    'updated_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: { query: UserQ, foreignKey: UserQ.id, sourceKey: SATUserQ.user_id },
  }),

  where: (
    {
      id,
      userId,
      email,
      parentEmail,
      type,
      inviteCode,
      crosswalkId,
      include = [],
      paranoid = true,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      email?: string | null | SQLFragment<string>
      parentEmail?: string | null | SQLFragment<string>
      type?: SATUserType | null
      inviteCode?: string | null | SQLFragment<string>
      crosswalkId?: string | null
      include?: IncludeAssociations
      paranoid?: boolean
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SATUserAttributes> => SQL`
        FROM ${SATUserQ}
        ${includeAssociationsToSql(SATUserQ, include)}
        WHERE
            ${id != null ? SQL`${SATUserQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${SATUserQ.user_id} = ${userId} AND` : SQL``}
            ${email != null ? SQL`${SATUserQ.email} = ${email} AND` : SQL``}
            ${parentEmail != null ? SQL`${SATUserQ.parent_email} = ${parentEmail} AND` : SQL``}
            ${type != null ? SQL`${SATUserQ.type} = ${type} AND` : SQL``}
            ${inviteCode != null ? SQL`${SATUserQ.invite_code} = ${inviteCode} AND` : SQL``}
            ${crosswalkId != null ? SQL`${SATUserQ.crosswalk_id} = ${crosswalkId} AND` : SQL``}
            ${paranoid ? SQL`${SATUserQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryLimitToSQL(limit)}
    `,

  isCBCrosswalkId: (type: 'tutor' | 'learner'): SQLFragment<boolean> =>
    SQL`(
        CASE
          WHEN ${SATUserQ.crosswalk_id}  !~ '^[0-9]+$' THEN TRUE
          ELSE CAST(${SATUserQ.crosswalk_id} AS BIGINT) < ${
            type === 'tutor' ? MINIMUM_TUTOR_CROSSWALK_ID : MINIMUM_LEARNER_CROSSWALK_ID
          }
        END
      )`,

  getCrosswalkId: (): SQLFragment<number> => SQL`CAST(${SATUserQ.crosswalk_id} AS BIGINT)`,

  isCBScoreVerified: (): SQLFragment<boolean> =>
    SQL`(${SATUserQ.eligible_math} OR ${SATUserQ.eligible_reading})`,

  hasType: (type: SATUserType): SQLFragment<boolean> => SQL`${SATUserQ.type} = ${type}`,

  hasEngagingBootcampRequirementWaived: (): SQLFragment<boolean> =>
    SQL`${SATUserQ.internal_state}->>'engaging_bootcamp_requirement_waived_at' IS NOT NULL`,

  hasCompletedRefresherTraining: (): SQLFragment<boolean> =>
    SQL`${SATUserQ.internal_state}->>'last_completed_tutor_refresher_training_at' IS NOT NULL`,

  hasCompletedMarch2025OneOnOneTraining: (): SQLFragment<boolean> =>
    SQL`${SATUserQ.internal_state}->>'completed_03_25_one_on_one_training_at' IS NOT NULL`,
}

export default SATUserQ
